import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight, CheckCircle, PlayCircle,
  Compass, Package, Lightbulb, ArrowsClockwise,
  Storefront, FileText, Envelope, BookOpen, Buildings, Megaphone,
  Calendar, Brain, Sparkle, Notebook, Heart,
} from '@phosphor-icons/react';
import { usePageSEO } from '@/utils/seo';
import {
  ORGANIZATION_SCHEMA, FOUNDER_SCHEMA, WEBSITE_SCHEMA, PRODUCT_SCHEMA,
} from '@/utils/schema';

/* ---------- Design tokens (mirrored from PayGBot dashboard) ---------- */
const PALETTE = {
  bg: '#0F1117',
  panel: '#171A22',
  card: '#1E222D',
  border: 'rgba(255,255,255,0.08)',
  text: '#FFFFFF',
  subtext: '#B8C0CC',
};

/* ---------- Reusable motion presets ---------- */
const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: 'easeOut' },
};

const stagger = (delay = 0) => ({
  initial: { opacity: 0, y: 18 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-60px' },
  transition: { duration: 0.55, ease: 'easeOut', delay },
});

const LandingPage = () => {
  usePageSEO({
    title: 'PayGBot — Your AI Business Strategist',
    description:
      'PayGBot is your AI Business Strategist. Build a business, create business assets, and know exactly what to do next — with personalized strategies and daily guidance built for real life.',
    path: '/',
    keywords:
      'AI business strategist, build a business, personalized business strategy, business assets, daily business guidance, next best action, business planning AI, PayGBot, business strategist for busy people, AI strategist',
    jsonLd: [ORGANIZATION_SCHEMA, FOUNDER_SCHEMA, WEBSITE_SCHEMA, PRODUCT_SCHEMA],
  });

  /* ---------- Content: How It Works ---------- */
  const howItWorksSteps = [
    {
      n: 1,
      icon: Compass,
      title: 'Build Your Business',
      desc: 'Discover the best business idea or refine the one you already have.',
    },
    {
      n: 2,
      icon: Package,
      title: 'Create What You Need',
      desc: 'Generate offers, sales pages, lead magnets, ebooks, business essentials, and marketing assets.',
    },
    {
      n: 3,
      icon: Lightbulb,
      title: 'Get Your Strategy',
      desc: 'Receive personalized business strategies, daily priorities, and marketing recommendations.',
    },
    {
      n: 4,
      icon: ArrowsClockwise,
      title: 'Keep Moving Forward',
      desc: 'PayGBot remembers your business and recommends your next best actions every day.',
    },
  ];

  /* ---------- Content: Week One Outcomes ---------- */
  const weekOneOutcomes = [
    'Business Strategy',
    'First Offer',
    'Sales Page',
    'Marketing Assets',
    'Lead Magnet',
    'Business Essentials',
    'Personalized Daily Priorities',
    'AI Business Strategist That Remembers Your Business',
  ];

  /* ---------- Content: Strategy Cycle ---------- */
  const cycleStages = ['Build', 'Create', 'Launch', 'Learn', 'Improve', 'Repeat'];

  /* ---------- Content: Features ---------- */
  const featureCards = [
    { icon: Compass,     name: 'Business Planning',       desc: 'Turn your ideas, skills, and experience into a business plan built around your life.' },
    { icon: Package,     name: 'Offer Creation',          desc: 'Package what you sell as a product, service, coaching, course, or template — never rewriting your business.' },
    { icon: Storefront,  name: 'Sales Pages',             desc: 'Convert visitors into buyers with sales pages generated from your business memory.' },
    { icon: FileText,    name: 'Lead Magnets',            desc: 'Build the free asset that captures the right audience for your specific business.' },
    { icon: BookOpen,    name: 'Ebooks',                  desc: 'Turn your expertise into a downloadable ebook — cover, chapters, and formatting included.' },
    { icon: Buildings,   name: 'Business Essentials',     desc: 'The founding documents, policies, and setup pieces most beginners forget until it costs them.' },
    { icon: Megaphone,   name: 'Marketing Strategy',      desc: 'Personalized marketing recommendations for your business type — local, online, product, or service.' },
    { icon: Calendar,    name: 'Daily Priorities',        desc: 'One clear next step every day so you never open the dashboard and wonder what to do.' },
    { icon: Brain,       name: 'Business Memory',         desc: 'PayGBot remembers your niche, audience, tone, and goals across every asset it creates.' },
    { icon: PlayCircle,  name: 'Content Ideas',           desc: 'Fresh, on-brand content prompts that reflect the business you actually run.' },
    { icon: Sparkle,     name: 'Future Recommendations',  desc: 'The more you build, the smarter your strategy becomes — recommendations sharpen over time.' },
  ];

  /* ---------- Content: Built for Busy People ---------- */
  const busyAudiences = [
    'Parents',
    'Side Hustlers',
    'Retirees',
    'Creators',
    'Makers',
    'Small Business Owners',
    'People Working After Hours',
  ];

  /* ---------- Content: Pricing Teaser (V2 — Business Strategist only) ---------- */
  const pricingPlans = [
    { name: 'Business Strategist', price: 49, description: 'Business Brain, Brand Brain, Business Kit, Market Intelligence, Opportunity Intelligence, plus 30 Business Credits. Build your business. Run it in minutes a day.', popular: true },
  ];

  return (
    <div
      className="min-h-screen text-white overflow-x-hidden"
      style={{ backgroundColor: PALETTE.bg }}
      data-testid="landing-page-v2"
    >
      {/* Ambient background glow (accent) */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div
          className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full opacity-20 blur-3xl"
          style={{ background: 'radial-gradient(closest-side, #10b981, transparent)' }}
        />
        <div
          className="absolute top-1/2 -right-40 w-[520px] h-[520px] rounded-full opacity-10 blur-3xl"
          style={{ background: 'radial-gradient(closest-side, #34d399, transparent)' }}
        />
      </div>

      {/* ==================== HEADER ==================== */}
      <header
        className="sticky top-0 z-50 backdrop-blur-md"
        style={{ backgroundColor: 'rgba(15,17,23,0.72)', borderBottom: `1px solid ${PALETTE.border}` }}
      >
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2" data-testid="landing-logo">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <span className="text-white font-bold text-sm">P</span>
              </div>
              <span className="text-lg font-bold text-white tracking-tight">PayGBot</span>
            </Link>
            <nav className="flex items-center space-x-1 sm:space-x-2">
              <Link to="/what-is-paygbot" className="hidden md:inline-flex px-3 py-1.5 text-sm text-slate-300 hover:text-white transition-colors">
                What Is PayGBot?
              </Link>
              <Link to="/founder-story" className="hidden md:inline-flex px-3 py-1.5 text-sm text-slate-300 hover:text-white transition-colors">
                Founder Story
              </Link>
              <Link to="/blog" className="hidden sm:inline-flex px-3 py-1.5 text-sm text-slate-300 hover:text-white transition-colors">
                Blog
              </Link>
              <Link to="/pricing" className="hidden sm:inline-flex px-3 py-1.5 text-sm text-slate-300 hover:text-white transition-colors">
                Pricing
              </Link>
              <Link
                to="/login"
                data-testid="landing-login-link"
                className="px-3 py-1.5 text-sm text-slate-300 hover:text-white transition-colors"
              >
                Login
              </Link>
              <Link
                to="/signup"
                data-testid="landing-signup-link"
                className="inline-flex items-center px-4 py-2 text-sm font-semibold text-white bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg shadow-md shadow-emerald-500/25 hover:shadow-lg hover:shadow-emerald-500/40 transition-all"
              >
                Start Free Trial
              </Link>
            </nav>
          </div>
        </div>
      </header>

      {/* ==================== HERO ==================== */}
      <section className="relative pt-20 pb-24 px-4 sm:px-6 lg:px-8" data-testid="landing-hero">
        <div className="max-w-[1200px] mx-auto grid lg:grid-cols-2 gap-12 items-center">
          {/* LEFT: Copy */}
          <motion.div {...fadeUp} className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10">
              <Sparkle size={14} weight="fill" className="text-emerald-400" />
              <span className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">
                Your AI Business Strategist
              </span>
            </div>

            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"
              data-testid="landing-hero-title"
            >
              Meet Your AI<br />
              <span className="bg-gradient-to-r from-emerald-400 to-green-500 bg-clip-text text-transparent">
                Business Strategist
              </span>
            </h1>

            <div
              className="text-lg sm:text-xl text-slate-300 leading-snug space-y-0.5"
              data-testid="landing-hero-subtitle"
            >
              <p>Build a business.</p>
              <p>Get a strategy.</p>
              <p>Know exactly what to do next.</p>
            </div>

            <p className="text-base text-slate-400 leading-relaxed max-w-xl">
              PayGBot helps busy people turn ideas, skills, and experience into real businesses by
              creating personalized strategies, business assets, and daily guidance.
            </p>

            <ul className="space-y-2 text-sm text-slate-400" data-testid="landing-hero-no-list">
              <li className="flex items-center gap-2">
                <CheckCircle size={16} weight="fill" className="text-emerald-400 flex-shrink-0" />
                No marketing degree
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle size={16} weight="fill" className="text-emerald-400 flex-shrink-0" />
                No expensive consultants
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle size={16} weight="fill" className="text-emerald-400 flex-shrink-0" />
                No guessing
              </li>
            </ul>

            <p className="text-base italic text-slate-300" data-testid="landing-supporting-statement">
              Business should build your life &mdash; not consume it.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pt-2">
              <Link
                to="/signup"
                data-testid="landing-cta-start"
                className="inline-flex items-center justify-center space-x-2 px-6 py-3.5 text-base font-semibold text-white bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-lg shadow-emerald-500/25 hover:shadow-xl hover:shadow-emerald-500/40 hover:scale-[1.02] transition-all"
              >
                <span>Start Building My Business</span>
                <ArrowRight size={18} weight="bold" />
              </Link>
              <a
                href="#how-it-works"
                data-testid="landing-cta-watch-demo"
                className="inline-flex items-center justify-center space-x-2 px-6 py-3.5 text-base font-medium text-slate-200 border rounded-xl transition-colors hover:bg-white/5"
                style={{ borderColor: PALETTE.border }}
              >
                <PlayCircle size={18} weight="regular" />
                <span>Watch Demo</span>
              </a>
            </div>
          </motion.div>

          {/* RIGHT: Dashboard mockup */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut', delay: 0.2 }}
            className="relative"
            data-testid="landing-hero-mockup"
          >
            <div className="relative rounded-[2rem] p-3 border border-white/10 bg-gradient-to-br from-slate-800 to-slate-900 shadow-2xl">
              {/* Laptop bezel */}
              <div className="rounded-2xl overflow-hidden border border-white/5 bg-black">
                {/* Simulated dashboard screenshot */}
                <div
                  className="relative aspect-[16/10] p-5"
                  style={{ backgroundColor: PALETTE.panel }}
                >
                  {/* Fake top bar */}
                  <div className="flex items-center justify-between pb-4 border-b border-white/[0.06]">
                    <div className="flex items-center gap-2">
                      <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-green-600 rounded-md" />
                      <span className="text-xs font-semibold text-white">PayGBot</span>
                    </div>
                    <div className="flex gap-1.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-400" />
                      <div className="w-2 h-2 rounded-full bg-yellow-400/60" />
                      <div className="w-2 h-2 rounded-full bg-red-400/60" />
                    </div>
                  </div>
                  {/* Fake content */}
                  <div className="pt-4 space-y-3">
                    <div className="space-y-1">
                      <p className="text-[10px] text-emerald-400 uppercase tracking-widest font-semibold">Today&rsquo;s next best action</p>
                      <p className="text-sm font-bold text-white">Approve your first offer &mdash; Winter Candle Trio</p>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {['Offer', 'Sales Page', 'Lead Magnet'].map((label) => (
                        <div
                          key={label}
                          className="rounded-lg p-2 border border-white/[0.06]"
                          style={{ backgroundColor: PALETTE.card }}
                        >
                          <p className="text-[9px] text-slate-400">Asset</p>
                          <p className="text-[11px] font-semibold text-white">{label}</p>
                          <div className="mt-1 h-1 rounded-full bg-emerald-500/30 overflow-hidden">
                            <div className="h-full bg-emerald-500 w-3/4" />
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      className="rounded-lg p-3 border border-emerald-500/20"
                      style={{ backgroundColor: 'rgba(16,185,129,0.08)' }}
                    >
                      <p className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">Strategy</p>
                      <p className="text-[11px] text-slate-200 leading-snug mt-1">
                        Package your candles as a Holiday Gift Trio. Post 3 launch teasers this week.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-lg p-2 border border-white/[0.06]" style={{ backgroundColor: PALETTE.card }}>
                        <p className="text-[9px] text-slate-400">This week</p>
                        <p className="text-[11px] font-semibold text-white">7 priorities</p>
                      </div>
                      <div className="rounded-lg p-2 border border-white/[0.06]" style={{ backgroundColor: PALETTE.card }}>
                        <p className="text-[9px] text-slate-400">Memory</p>
                        <p className="text-[11px] font-semibold text-white">Ready</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating strategy chip */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -left-6 top-16 hidden sm:flex items-center gap-2 px-3 py-2 rounded-xl border shadow-xl"
                style={{ backgroundColor: PALETTE.card, borderColor: PALETTE.border }}
              >
                <Brain size={16} weight="fill" className="text-emerald-400" />
                <span className="text-xs text-white">Business memory active</span>
              </motion.div>
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                className="absolute -right-4 bottom-12 hidden sm:flex items-center gap-2 px-3 py-2 rounded-xl border shadow-xl"
                style={{ backgroundColor: PALETTE.card, borderColor: PALETTE.border }}
              >
                <Sparkle size={16} weight="fill" className="text-emerald-400" />
                <span className="text-xs text-white">Next step ready</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== HOW IT WORKS ==================== */}
      <section
        id="how-it-works"
        className="py-24 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: PALETTE.panel, borderTop: `1px solid ${PALETTE.border}` }}
        data-testid="how-it-works-section"
      >
        <div className="max-w-[1200px] mx-auto">
          <motion.div {...fadeUp} className="text-center space-y-3 mb-14">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight"
              data-testid="how-it-works-title"
            >
              How PayGBot Works
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto">
              Four steps that turn an idea into a business you understand, a strategy you can follow, and a next action you can start today.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {howItWorksSteps.map((step, idx) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.n}
                  {...stagger(idx * 0.08)}
                  className="group relative rounded-2xl p-6 border transition-all hover:border-emerald-500/40"
                  style={{ backgroundColor: PALETTE.card, borderColor: PALETTE.border }}
                  data-testid={`how-it-works-step-${step.n}`}
                >
                  <div className="flex items-center justify-between mb-5">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-gradient-to-br from-emerald-500/20 to-green-600/10 border border-emerald-500/30 text-emerald-400 group-hover:from-emerald-500/30 group-hover:to-green-600/20 transition-all">
                      <Icon size={20} weight="fill" />
                    </div>
                    <span className="text-4xl font-black text-white/[0.06] leading-none">{String(step.n).padStart(2, '0')}</span>
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">{step.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== WEEK ONE OUTCOMES ==================== */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" data-testid="week1-section">
        <div className="max-w-[1200px] mx-auto">
          <motion.div {...fadeUp} className="text-center space-y-3 mb-14">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight"
              data-testid="week1-outcome-title"
            >
              What You&rsquo;ll Have After Your First Week
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto">
              A real starting point &mdash; not homework, not modules, not more courses to finish.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {weekOneOutcomes.map((item, idx) => (
              <motion.div
                key={item}
                {...stagger((idx % 4) * 0.05)}
                className="flex items-start gap-3 rounded-xl p-4 border transition-colors hover:border-emerald-500/40"
                style={{ backgroundColor: PALETTE.card, borderColor: PALETTE.border }}
                data-testid={`week1-item-${idx}`}
              >
                <CheckCircle
                  size={22}
                  weight="fill"
                  className="text-emerald-400 flex-shrink-0 mt-0.5"
                />
                <span className="text-sm sm:text-base text-white font-medium leading-snug">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== BUSINESS STRATEGY CYCLE ==================== */}
      <section
        className="py-24 px-4 sm:px-6 lg:px-8 relative"
        style={{ backgroundColor: PALETTE.panel, borderTop: `1px solid ${PALETTE.border}` }}
        data-testid="strategy-cycle-section"
      >
        <div className="max-w-[1200px] mx-auto">
          <motion.div {...fadeUp} className="text-center space-y-3 mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight">
              Your Business Strategy Cycle
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto">
              Every action helps PayGBot understand your business better so your future recommendations become even more personalized.
            </p>
          </motion.div>

          {/* Circular arrangement (desktop) + linear (mobile) */}
          <div className="relative mx-auto max-w-2xl">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {cycleStages.map((stage, idx) => (
                <motion.div
                  key={stage}
                  {...stagger(idx * 0.08)}
                  className="relative rounded-2xl p-5 border text-center overflow-hidden group hover:border-emerald-500/40 transition-colors"
                  style={{ backgroundColor: PALETTE.card, borderColor: PALETTE.border }}
                  data-testid={`cycle-stage-${stage.toLowerCase()}`}
                >
                  <div className="w-10 h-10 mx-auto mb-3 rounded-full flex items-center justify-center bg-gradient-to-br from-emerald-500 to-green-600 shadow-lg shadow-emerald-500/25">
                    <span className="text-white font-bold text-sm">{idx + 1}</span>
                  </div>
                  <p className="text-lg font-semibold text-white">{stage}</p>
                  <motion.div
                    className="absolute inset-0 rounded-2xl pointer-events-none"
                    style={{ boxShadow: 'inset 0 0 30px rgba(16,185,129,0.15)' }}
                    animate={{ opacity: [0, 0.5, 0] }}
                    transition={{ duration: 4, delay: idx * 0.5, repeat: Infinity }}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ==================== FEATURES ==================== */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" data-testid="features-section">
        <div className="max-w-[1200px] mx-auto">
          <motion.div {...fadeUp} className="text-center space-y-3 mb-14">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight"
              data-testid="features-title"
            >
              Everything Your Business Strategist Helps You Create
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto">
              Every asset PayGBot generates reflects the business you actually run &mdash; never a generic template.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {featureCards.map((f, idx) => {
              const Icon = f.icon;
              return (
                <motion.div
                  key={f.name}
                  {...stagger((idx % 3) * 0.06)}
                  className="rounded-2xl p-6 border transition-all hover:border-emerald-500/40 hover:-translate-y-0.5"
                  style={{ backgroundColor: PALETTE.card, borderColor: PALETTE.border }}
                  data-testid={`feature-card-${idx}`}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 mb-4">
                    <Icon size={18} weight="fill" />
                  </div>
                  <h3 className="text-base font-semibold text-white mb-2">{f.name}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ==================== BUILT FOR BUSY PEOPLE ==================== */}
      <section
        className="py-24 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: PALETTE.panel, borderTop: `1px solid ${PALETTE.border}` }}
        data-testid="busy-people-section"
      >
        <div className="max-w-[1200px] mx-auto text-center">
          <motion.div {...fadeUp} className="space-y-3 mb-10">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight">
              Built for Busy People
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto">
              PayGBot was designed for the version of you working after hours, between school runs, or during a lunch break.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="flex flex-wrap justify-center gap-3">
            {busyAudiences.map((who) => (
              <span
                key={who}
                className="px-5 py-2.5 rounded-full text-sm font-medium text-slate-200 border transition-colors hover:border-emerald-500/40 hover:text-white"
                style={{ backgroundColor: PALETTE.card, borderColor: PALETTE.border }}
                data-testid={`audience-chip-${who.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {who}
              </span>
            ))}
          </motion.div>

          <motion.div {...fadeUp} className="grid sm:grid-cols-4 gap-4 max-w-3xl mx-auto mt-12">
            {[
              { title: 'Minutes a day', desc: 'Short focused sessions' },
              { title: 'No tech skills', desc: 'Straightforward flows' },
              { title: 'Clear guidance', desc: 'Always know the next step' },
              { title: 'Real assets', desc: 'Everything is saved for you' },
            ].map((item, i) => (
              <div
                key={i}
                className="rounded-xl p-5 border text-left"
                style={{ backgroundColor: PALETTE.card, borderColor: PALETTE.border }}
              >
                <CheckCircle size={20} weight="fill" className="text-emerald-400 mb-3" />
                <p className="text-sm font-semibold text-white">{item.title}</p>
                <p className="text-xs text-slate-400 mt-1">{item.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ==================== FOUNDER STORY ==================== */}
      <section className="py-24 px-4 sm:px-6 lg:px-8" data-testid="founder-story-section">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fadeUp} className="text-center space-y-3 mb-10">
            <span
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10"
              data-testid="founder-story-eyebrow"
            >
              <Heart size={14} weight="fill" className="text-emerald-400" />
              <span className="text-xs uppercase tracking-widest text-emerald-300 font-semibold">
                From the founder
              </span>
            </span>
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight"
              data-testid="founder-story-title"
            >
              Why I Built PayGBot
            </h2>
          </motion.div>

          <motion.div
            {...fadeUp}
            className="rounded-3xl p-8 sm:p-10 border space-y-5 text-slate-300 leading-relaxed"
            style={{ backgroundColor: PALETTE.card, borderColor: PALETTE.border }}
            data-testid="founder-story-body"
          >
            <p className="text-base sm:text-lg text-white">
              I created PayGBot to become the business strategist I wish I&rsquo;d had while building businesses, working full-time, and raising a family.
            </p>
            <p>I know what it&rsquo;s like to have ideas but not enough time.</p>
            <p>To know you&rsquo;re capable of more but not know where to focus next.</p>
            <p>PayGBot was built to remove the guesswork.</p>
            <p>
              It helps busy people make better business decisions by providing clear strategies, personalized guidance, and the tools they need to move forward with confidence.
            </p>
            <p className="italic text-emerald-300 pt-2">&mdash; Vickie Mcglawn, Founder</p>
          </motion.div>

          <motion.div {...fadeUp} className="text-center mt-8">
            <Link
              to="/founder-story"
              data-testid="founder-story-cta"
              className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 font-medium"
            >
              Read the full founder story
              <ArrowRight size={16} weight="bold" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ==================== PRICING TEASER ==================== */}
      <section
        className="py-24 px-4 sm:px-6 lg:px-8"
        style={{ backgroundColor: PALETTE.panel, borderTop: `1px solid ${PALETTE.border}` }}
        data-testid="pricing-teaser-section"
      >
        <div className="max-w-[1200px] mx-auto">
          <motion.div {...fadeUp} className="text-center space-y-3 mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight" data-testid="pricing-teaser-title">
              Build and Run Your Business for $49/month
            </h2>
            <p className="text-base text-slate-400">
              Start your 7-day free trial today. One plan, everything included.
            </p>
          </motion.div>

          <div className="max-w-md mx-auto">
            {pricingPlans.map((plan, idx) => (
              <motion.div
                key={plan.name}
                {...stagger(idx * 0.08)}
                data-testid={`pricing-teaser-${plan.name.toLowerCase().replace(/\s+/g, '-')}`}
                className={`relative rounded-2xl p-8 border transition-all ${
                  plan.popular
                    ? 'border-emerald-500/50 shadow-xl shadow-emerald-500/10'
                    : 'hover:border-emerald-500/30'
                }`}
                style={{ backgroundColor: PALETTE.card, borderColor: plan.popular ? undefined : PALETTE.border }}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="px-3 py-1 bg-gradient-to-br from-emerald-500 to-green-600 text-white text-xs font-semibold rounded-full shadow-md shadow-emerald-500/30">
                      7-Day Free Trial
                    </span>
                  </div>
                )}
                <div className="text-center space-y-4">
                  <h3 className="text-xl font-semibold text-white">{plan.name}</h3>
                  <div>
                    <span className="text-5xl font-bold text-white">${plan.price}</span>
                    <span className="text-slate-400 text-sm">/month</span>
                  </div>
                  <p className="text-sm text-slate-400 leading-relaxed">{plan.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link
              to="/pricing"
              data-testid="view-full-pricing-btn"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-slate-200 border rounded-lg hover:bg-white/5 transition-colors"
              style={{ borderColor: PALETTE.border }}
            >
              <span>View Full Pricing</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div {...fadeUp} className="relative rounded-3xl p-10 sm:p-14 border overflow-hidden"
            style={{ backgroundColor: PALETTE.card, borderColor: PALETTE.border }}
          >
            <div
              className="absolute inset-0 pointer-events-none opacity-40"
              style={{
                background: 'radial-gradient(circle at 50% 0%, rgba(16,185,129,0.35), transparent 65%)',
              }}
            />
            <div className="relative space-y-6">
              <Notebook size={40} weight="fill" className="text-emerald-400 mx-auto" />
              <h2
                className="text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight"
                data-testid="landing-final-headline"
              >
                Stop Wondering What To Do Next
              </h2>
              <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl mx-auto" data-testid="landing-final-subhead">
                Build your business with an AI Business Strategist that remembers your goals, creates what you need, and helps you decide your next best move.
              </p>
              <div>
                <Link
                  to="/signup"
                  data-testid="landing-cta-bottom"
                  className="inline-flex items-center justify-center space-x-2 px-7 py-4 text-base font-semibold text-white bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl shadow-xl shadow-emerald-500/30 hover:shadow-2xl hover:shadow-emerald-500/50 hover:scale-[1.02] transition-all"
                >
                  <span>Start Building My Business</span>
                  <ArrowRight size={18} weight="bold" />
                </Link>
                <p className="text-sm text-slate-400 mt-4">
                  Start your free trial today.<br className="sm:hidden" /> Cancel anytime.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ==================== FOOTER ==================== */}
      <footer
        className="border-t"
        style={{ borderColor: PALETTE.border, backgroundColor: '#0B0D13' }}
        data-testid="landing-footer"
      >
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-green-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-sm">P</span>
                </div>
                <span className="text-base font-bold text-white">PayGBot</span>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">
                Your AI Business Strategist. Personalized strategy, business assets, and daily guidance &mdash; built for real life.
              </p>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/" className="text-slate-400 hover:text-emerald-400 transition-colors">Home</Link></li>
                <li><Link to="/what-is-paygbot" className="text-slate-400 hover:text-emerald-400 transition-colors">What Is PayGBot?</Link></li>
                <li><Link to="/pricing" className="text-slate-400 hover:text-emerald-400 transition-colors">Pricing</Link></li>
                <li><Link to="/signup" className="text-slate-400 hover:text-emerald-400 transition-colors">Start Building</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white mb-4">Resources</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="text-slate-400 hover:text-emerald-400 transition-colors">Blog</Link></li>
                <li><Link to="/blog/why-we-chose-dark-mode-for-paygbot" className="text-slate-400 hover:text-emerald-400 transition-colors">Why We Chose Dark Mode</Link></li>
                <li><Link to="/blog/what-is-an-ai-business-operating-system" className="text-slate-400 hover:text-emerald-400 transition-colors">The AI Strategist Approach</Link></li>
                <li><Link to="/blog/why-busy-people-need-simpler-business-systems" className="text-slate-400 hover:text-emerald-400 transition-colors">Simpler Systems for Busy People</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white mb-4">About</h3>
              <ul className="space-y-2 text-sm">
                <li><Link to="/founder-story" className="text-slate-400 hover:text-emerald-400 transition-colors">Founder Story</Link></li>
                <li><Link to="/blog/how-to-build-a-business-during-difficult-seasons-of-life" className="text-slate-400 hover:text-emerald-400 transition-colors">Building During Hard Seasons</Link></li>
                <li><Link to="/blog/how-automation-helps-families-build-income-more-flexibly" className="text-slate-400 hover:text-emerald-400 transition-colors">Automation for Families</Link></li>
              </ul>
            </div>
          </div>
          <div
            className="mt-12 pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-t"
            style={{ borderColor: PALETTE.border }}
          >
            <p className="text-xs text-slate-500">
              &copy; {new Date().getFullYear()} PayGBot. Built by Vickie Mcglawn for real people, real lives, and real businesses.
            </p>
            <p className="text-xs text-slate-500">Your AI Business Strategist.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
